import { useQuery } from "@apollo/client/react";
import { ENERGY_DATA } from "@/hooks";
import { enqueueSnackbar } from "notistack";
import { useEffect } from "react";
import type { QueryEnergyDataArgs } from "@/types/graphql";

type EnergyDataVariables = Partial<QueryEnergyDataArgs>;

/**
 * Custom hook to fetch energy data.
 * @param {object} variables - The variables to be passed to the query.
 * @param {Function} [onCompleted] - Callback function to be called when the query completes successfully.
 * @param {Function} [onError] - Callback function to be called when an error occurs during the query.
 * @returns {{data: Array<{id: string, value: number, timestamp: string}>, total: number, loading: boolean}} - The fetched energy data, total count, and the loading state.
 */
export function useEnergyData(
  variables: EnergyDataVariables,
  onCompleted: (data?: any) => void = () => {},
  onError: (error?: any) => void = () => {},
) {
  // Remove country from variables if it's null
  const queryVariables = { ...variables };
  if (queryVariables.country === null) {
    delete queryVariables.country;
  }

  const skip =
    !queryVariables.customer ||
    !queryVariables.group ||
    !queryVariables.meterType ||
    !queryVariables.address ||
    !queryVariables.meteringPointId ||
    !queryVariables.fromDate ||
    !queryVariables.toDate ||
    !queryVariables.resolution;

  const { data, loading, error } = useQuery<any>(ENERGY_DATA, {
    variables: queryVariables,
    skip,
    errorPolicy: "all",
  });

  useEffect(() => {
    if (data?.energyData) {
      onCompleted(data.energyData);
    }
  }, [data, onCompleted]);

  useEffect(() => {
    if (error) {
      onError(error);

      enqueueSnackbar(error.message, {
        variant: "error",
      });
    }
  }, [error, onError]);

  return {
    data: data?.energyData?.data || [],
    total: data?.energyData?.total || 0,
    loading,
  };
}
